import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  User, Trophy, TrendingUp, DollarSign, Users, 
  MapPin, Calendar, Award, Instagram, FileText,
  Briefcase, Target, CheckCircle, Star
} from 'lucide-react';

interface TalentApplicationProfileProps {
  talent: {
    id: string;
    name: string;
    first_name?: string;
    last_name?: string;
    photo?: string;
    sport?: string;
    position?: string;
    team_name?: string;
    total_followers?: number;
    total_revenue?: number;
    total_holdings?: number;
    avg_business_impact?: number;
    tier?: string;
    status?: string;
    height?: string;
    weight?: string;
    nationality?: string;
    competition_level?: string;
    college_university?: string;
    draft_round?: number;
    draft_year?: number;
    achievements?: string[];
    career_highlights?: any[];
    sponsors?: any[];
    instagram_handle?: string;
    media_kit_url?: string;
    stats?: any;
    bio?: string;
    location?: string;
    is_verified?: boolean;
    avatar_url?: string;
  };
}

export const TalentApplicationProfile: React.FC<TalentApplicationProfileProps> = ({ talent }) => {
  // Calculate match score based on available metrics
  const calculateMatchScore = () => {
    let score = 50; // Base score
    
    if (talent.total_followers && talent.total_followers > 10000) score += 15;
    if (talent.total_followers && talent.total_followers > 50000) score += 10;
    if (talent.tier === 'Platinum') score += 20;
    else if (talent.tier === 'Gold') score += 15;
    else if (talent.tier === 'Silver') score += 10;
    if (talent.is_verified) score += 10;
    if (talent.total_holdings && talent.total_holdings > 0) score += 10;
    if (talent.total_revenue && talent.total_revenue > 0) score += 10;
    if (talent.avg_business_impact && talent.avg_business_impact > 50) score += 15;
    
    return Math.min(score, 100);
  };

  const matchScore = calculateMatchScore();

  const getTierColor = (tier?: string) => {
    switch (tier) {
      case 'Platinum': return 'bg-purple-500/10 text-purple-700 border-purple-500/20';
      case 'Gold': return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20';
      case 'Silver': return 'bg-slate-500/10 text-slate-700 border-slate-500/20';
      default: return 'bg-secondary';
    }
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-orange-600';
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0">
              <Avatar className="h-32 w-32">
                <AvatarImage src={talent.avatar_url || talent.photo} alt={talent.name} />
                <AvatarFallback className="text-2xl">
                  <User className="h-16 w-16" />
                </AvatarFallback>
              </Avatar>
            </div>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-start gap-2 mb-3">
                <h2 className="text-2xl font-bold">{talent.name}</h2>
                {talent.is_verified && (
                  <Badge variant="secondary" className="bg-blue-500/10 text-blue-700 border-blue-500/20">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                )}
                {talent.tier && (
                  <Badge className={getTierColor(talent.tier)}>
                    <Star className="h-3 w-3 mr-1" />
                    {talent.tier}
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {talent.sport && (
                  <div className="flex items-center gap-2 text-sm">
                    <Trophy className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{talent.sport}</span>
                    {talent.position && <span className="text-muted-foreground">• {talent.position}</span>}
                  </div>
                )}
                {talent.team_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span>{talent.team_name}</span>
                  </div>
                )}
                {talent.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{talent.location}</span>
                  </div>
                )}
                {talent.competition_level && (
                  <div className="flex items-center gap-2 text-sm">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <span className="capitalize">{talent.competition_level}</span>
                  </div>
                )}
              </div>

              {/* Match Score */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Match Score</span>
                  <span className={`text-2xl font-bold ${getMatchScoreColor(matchScore)}`}>
                    {matchScore}%
                  </span>
                </div>
                <Progress value={matchScore} className="h-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Followers</p>
                <p className="text-lg font-bold">
                  {talent.total_followers ? talent.total_followers.toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Revenue</p>
                <p className="text-lg font-bold">
                  ${talent.total_revenue ? talent.total_revenue.toLocaleString() : '0'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Partnerships</p>
                <p className="text-lg font-bold">{talent.total_holdings || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Impact Score</p>
                <p className="text-lg font-bold">
                  {talent.avg_business_impact ? talent.avg_business_impact.toFixed(1) : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Information Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="athletic">Athletic</TabsTrigger>
          <TabsTrigger value="career">Career</TabsTrigger>
          <TabsTrigger value="social">Social</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>About</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {talent.bio || 'No bio available.'}
              </p>
            </CardContent>
          </Card>

          {talent.achievements && talent.achievements.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Achievements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {talent.achievements.map((achievement, index) => (
                    <Badge key={index} variant="secondary">
                      {achievement}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {talent.sponsors && talent.sponsors.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Current Sponsors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {talent.sponsors.map((sponsor: any, index: number) => (
                    <Badge key={index} variant="outline">
                      {typeof sponsor === 'string' ? sponsor : sponsor.name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="athletic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Athletic Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {talent.height && (
                  <div>
                    <p className="text-sm text-muted-foreground">Height</p>
                    <p className="font-medium">{talent.height}</p>
                  </div>
                )}
                {talent.weight && (
                  <div>
                    <p className="text-sm text-muted-foreground">Weight</p>
                    <p className="font-medium">{talent.weight}</p>
                  </div>
                )}
                {talent.nationality && (
                  <div>
                    <p className="text-sm text-muted-foreground">Nationality</p>
                    <p className="font-medium">{talent.nationality}</p>
                  </div>
                )}
                {talent.college_university && (
                  <div>
                    <p className="text-sm text-muted-foreground">College/University</p>
                    <p className="font-medium">{talent.college_university}</p>
                  </div>
                )}
                {talent.draft_year && (
                  <div>
                    <p className="text-sm text-muted-foreground">Draft Year</p>
                    <p className="font-medium">
                      {talent.draft_year}
                      {talent.draft_round && ` - Round ${talent.draft_round}`}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="career" className="space-y-4">
          {talent.career_highlights && talent.career_highlights.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Career Highlights</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {talent.career_highlights.map((highlight: any, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <Trophy className="h-4 w-4 text-primary mt-0.5" />
                      <span className="text-sm">
                        {typeof highlight === 'string' ? highlight : highlight.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Business Impact</span>
                    <span className="text-sm font-bold">
                      {talent.avg_business_impact ? `${talent.avg_business_impact.toFixed(0)}%` : 'N/A'}
                    </span>
                  </div>
                  <Progress value={talent.avg_business_impact || 0} className="h-2" />
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Total Deals</p>
                    <p className="text-xl font-bold">{talent.total_holdings || 0}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Revenue Generated</p>
                    <p className="text-xl font-bold">
                      ${talent.total_revenue ? (talent.total_revenue / 1000).toFixed(0) + 'K' : '0'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="social" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Social Presence</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {talent.instagram_handle && (
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Instagram className="h-5 w-5 text-pink-600" />
                    <div>
                      <p className="font-medium">@{talent.instagram_handle}</p>
                      <p className="text-xs text-muted-foreground">Instagram</p>
                    </div>
                  </div>
                  {talent.total_followers && (
                    <div className="text-right">
                      <p className="font-bold">{talent.total_followers.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Followers</p>
                    </div>
                  )}
                </div>
              )}

              {talent.media_kit_url && (
                <a 
                  href={talent.media_kit_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                >
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">View Media Kit</p>
                    <p className="text-xs text-muted-foreground">Press materials and additional information</p>
                  </div>
                </a>
              )}

              {!talent.instagram_handle && !talent.media_kit_url && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No social media information available
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
